import {
  getMandibularTeethForPartialDentureInfo,
  getMaxillaryTeethForPartialDentureInfo,
  getRemovableItemFromArch,
} from "../product.utils";
import { RemovableItem, Order, Product } from "../../types/interfaces";
import { ItemType } from "../../types/enums";
import { sortTeethAsc } from "../item.utils";

export type InformationMappedValues = Record<string, unknown> & {
  teeths: {
    missingTeeth: number[];
    toExtract: number[];
  };
  isReplacement: boolean;
};

const getRemovableItemSavedValues = (
  removableItem: RemovableItem | undefined,
  missingTeeth: number[],
  toIncludeTeeth: number[]
) => {
  const removableItemInfo: Record<string, unknown> = {};
  const partialDentureInfo: Record<string, unknown> = {};
  if (removableItem) {
    if (removableItem?.workflowType !== null) {
      removableItemInfo.workflowType = removableItem.workflowType;
    }
    if (
      removableItem?.partialDentureOrderType !== null &&
      removableItem?.partialDentureOrderType !== undefined
    ) {
      removableItemInfo.partialDentureOrderType =
        removableItem.partialDentureOrderType;
    }
    // Preserve rdvNumber: the deleteMany/create in updateInformation otherwise
    // resets it to null, which breaks the previous-step lookup in billing
    // (wrong "Finition Directe" price instead of "Finition après essayage").
    if (
      removableItem?.rdvNumber !== null &&
      removableItem?.rdvNumber !== undefined
    ) {
      removableItemInfo.rdvNumber = removableItem.rdvNumber;
    }
    if (removableItem?.itemMaterial !== null) {
      removableItemInfo.itemMaterial = {
        connect: {
          id: removableItem?.itemMaterial?.id,
        },
      };
    }
    if (removableItem?.teethShade !== null) {
      removableItemInfo.teethShade = {
        connect: {
          id: removableItem?.teethShade?.id,
        },
      };
    }
    if (removableItem?.gingivaShade !== null) {
      removableItemInfo.gingivaShade = {
        connect: {
          id: removableItem?.gingivaShade?.id,
        },
      };
    }
    if (
      removableItem?.partialDenture?.hooks.length > 0 &&
      removableItem?.partialDenture?.hooks.some(
        hook => hook.hooksMaterial !== null
      )
    ) {
      const hooks = removableItem?.partialDenture?.hooks.map(hook => ({
        hooksMaterial: {
          connect: {
            id: hook.hooksMaterial?.id,
          },
        },
        hooksPosition: {
          set: sortTeethAsc(
            hook.hooksPosition.filter(
              position =>
                ![...missingTeeth, ...toIncludeTeeth].includes(position)
            )
          ),
        },
      }));

      partialDentureInfo.hooks = {
        create: hooks,
      };
    }
    if (removableItem?.partialDenture?.palatalMetalBand !== null) {
      partialDentureInfo.palatalMetalBand =
        removableItem?.partialDenture?.palatalMetalBand;
    }
    if (removableItem?.partialDenture?.metalMesh !== null) {
      partialDentureInfo.metalMesh = removableItem?.partialDenture?.metalMesh;
    }
    if (removableItem?.partialDenture?.customTray !== null) {
      partialDentureInfo.customTray = removableItem?.partialDenture?.customTray;
    }
    if (removableItem?.partialDenture?.lingualMetalBand !== null) {
      partialDentureInfo.lingualMetalBand =
        removableItem?.partialDenture?.lingualMetalBand;
    }
  }
  return {
    removableItemInfo,
    partialDentureInfo,
  };
};

const createPartialDentureItemsGraphql = ({
  missingTeeth,
  toIncludeTeeth,
  isReplacement,
  removableItem,
}: {
  missingTeeth: number[];
  toIncludeTeeth: number[];
  isReplacement: boolean;
  removableItem: RemovableItem | undefined;
}) => {
  const values = [];
  const { removableItemInfo, partialDentureInfo } = getRemovableItemSavedValues(
    removableItem,
    missingTeeth,
    toIncludeTeeth
  );
  if ([...missingTeeth, ...toIncludeTeeth].length > 0) {
    values.push({
      itemType: ItemType.PARTIAL_DENTURE,
      teeth: sortTeethAsc([...missingTeeth, ...toIncludeTeeth]),
      teethToManufacture: {
        set: toIncludeTeeth,
      },
      isReplacement: isReplacement,
      ...removableItemInfo,
      partialDenture: {
        create: {
          missingTeeth: {
            set: missingTeeth,
          },
          ...partialDentureInfo,
        },
      },
    });
  }
  return values;
};

export const updateInformation = async (
  updateOrder: (data: {
    variables: {
      data: any;
      where: { id: string };
    };
  }) => Promise<Order>,
  mappedValues: InformationMappedValues,
  orderId: string,
  product: Product
) => {
  if (!product.removableItem || !Array.isArray(product.removableItem)) {
    throw new Error("No valid removable items found in product");
  }
  const {
    missingTeeth: missingTeethMandibular,
    toIncludeTeeth: toIncludeTeethMandibular,
  } = getMandibularTeethForPartialDentureInfo(
    mappedValues.teeths.missingTeeth,
    mappedValues.teeths.toExtract
  );
  const {
    missingTeeth: missingTeethMaxillary,
    toIncludeTeeth: toIncludeTeethMaxillary,
  } = getMaxillaryTeethForPartialDentureInfo(
    mappedValues.teeths.missingTeeth,
    mappedValues.teeths.toExtract
  );

  const createValues = () => {
    const returnValues = [];
    const removableItem = getRemovableItemFromArch(product.removableItem);
    if (
      (missingTeethMandibular?.length ?? 0) > 0 ||
      (toIncludeTeethMandibular?.length ?? 0) > 0
    ) {
      returnValues.push(
        ...createPartialDentureItemsGraphql({
          missingTeeth: missingTeethMandibular,
          toIncludeTeeth: toIncludeTeethMandibular,
          isReplacement: mappedValues.isReplacement,
          removableItem: removableItem.mandibularTeeth,
        })
      );
    }

    if (
      (missingTeethMaxillary?.length ?? 0) > 0 ||
      (toIncludeTeethMaxillary?.length ?? 0) > 0
    ) {
      returnValues.push(
        ...createPartialDentureItemsGraphql({
          missingTeeth: missingTeethMaxillary,
          toIncludeTeeth: toIncludeTeethMaxillary,
          isReplacement: mappedValues.isReplacement,
          removableItem: removableItem.maxillaryTeeth,
        })
      );
    }

    return returnValues;
  };

  const variables = {
    data: {
      products: {
        update: {
          data: {
            removableItem: {
              deleteMany: {
                productId: {
                  equals: product.id,
                },
              },
              create: createValues(),
            },
          },
          where: {
            id: product.id,
          },
        },
      },
    },
    where: {
      id: orderId,
    },
  };
  try {
    return await updateOrder({ variables: variables });
  } catch (error) {
    console.error("Failed to update partial denture information:", error);
    throw error;
  }
};
